import { Box, Button, Container, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import Loader from '../../components/Loader';
import { Input } from '../../components/formComponents';
import TextBox from '../../components/formComponents/TextArea';
import {
  useCreateNpsSurveyResultMutation,
  useHasNpsSurveyResultQuery,
} from '../../gql/generated/graphql';
import { useRemoveSearchParams } from '../../hooks';
import hotjar, { HotjarEvents } from '../../services/analytics/hotjar';
import { emailValidationRequired } from '../../utils/validation/email';
import { nameValidationMax30 } from '../../utils/validation/names';
import Gauge from './Gauge';
import LowScoreModal from './LowScoreModal';

type FormValues = {
  feedback: string;
  rating: number;
  first_name?: string;
  last_name?: string;
  email?: string;
};

const WeListen = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const gaugeRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dealId = searchParams.get('deal_id');
  const email = searchParams.get('email');
  useRemoveSearchParams(['email']);

  const hasDealId = !!dealId;
  const parsedDealId = dealId ? parseInt(dealId, 10) : undefined;

  const initialValues = useMemo<FormValues>(
    () => ({
      feedback: '',
      rating: 10,
      first_name: '',
      last_name: '',
      email: email || '',
    }),
    [email],
  );

  const validationSchema = Yup.object().shape({
    rating: Yup.number().nullable().required('Rating is required'),
    feedback: Yup.string().required('Feedback is required'),
    ...(hasDealId
      ? {}
      : {
          first_name: nameValidationMax30,
          last_name: nameValidationMax30,
          email: emailValidationRequired,
        }),
  });

  const {
    data: hasNpsSurveyResult,
    loading: isCheckingExisting,
    error: hasNpsSurveyResultError,
  } = useHasNpsSurveyResultQuery({
    variables: { dealId: parsedDealId || 0 },
    skip: !dealId,
    context: {
      isErrorHandled: true,
    },
  });

  const [
    createNpsSurveyResult,
    { data: createNpsSurveyResultData, error: createNpsSurveyResultError },
  ] = useCreateNpsSurveyResultMutation();

  const doSubmit = async (values: FormValues) => {
    await createNpsSurveyResult({
      variables: {
        input: {
          deal_id: parsedDealId,
          email: hasDealId ? email : values.email,
          rating: values.rating,
          feedback: values.feedback,
          first_name: hasDealId ? undefined : values.first_name?.trim(),
          last_name: hasDealId ? undefined : values.last_name?.trim(),
        },
      },
    });
  };

  const handleSubmit = async (values: FormValues) => {
    if (values.rating <= 3) {
      setPendingValues(values);
      onOpen();
      return;
    }
    await doSubmit(values);
  };

  const handleModalFixIt = () => {
    onClose();
    gaugeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleModalConfirm = async () => {
    if (pendingValues) {
      setIsConfirming(true);
      try {
        await doSubmit(pendingValues);
      } finally {
        setIsConfirming(false);
      }
    }
    onClose();
  };

  useEffect(() => {
    if (dealId) {
      hotjar.identify(null, { deal_id: dealId });
    }
    hotjar.track(HotjarEvents.WeListen);
  }, [dealId]);

  if (!!createNpsSurveyResultError || !!hasNpsSurveyResultError) {
    return (
      <Container maxW="container.sm" py={8}>
        <Box textAlign="center">
          <Text fontWeight="bold" fontSize={{ base: '2xl', md: '3xl' }} color="red.400" mb={4}>
            Oops! Something went wrong
          </Text>
          {!createNpsSurveyResultError && (
            <Text color="white" fontSize="lg" mb={6}>
              Please use a valid survey link to access this page.
            </Text>
          )}
        </Box>
      </Container>
    );
  }

  if (isCheckingExisting) {
    return <Loader />;
  }

  if (hasNpsSurveyResult?.hasNpsSurveyResult) {
    return (
      <Container maxW="container.sm" py={8}>
        <Box textAlign="center">
          <Text fontWeight="bold" fontSize={{ base: '2xl', md: '3xl' }} color="yellow.400" mb={4}>
            Survey Already Completed
          </Text>
          <Text color="white" fontSize="lg" mb={6}>
            You have already submitted feedback for this deal. Thank you for your input!
          </Text>
        </Box>
      </Container>
    );
  }

  if (createNpsSurveyResultData?.createNpsSurveyResult) {
    return (
      <Container maxW="container.sm" py={8}>
        <Box textAlign="center">
          <Text fontWeight="bold" fontSize={{ base: '2xl', md: '3xl' }} color="green.400" mb={4}>
            Thank you for your feedback!
          </Text>
          <Text color="white" fontSize="lg" mb={6}>
            Your survey has been submitted successfully. We appreciate you taking the time to share
            your experience with us.
          </Text>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.sm" py={8}>
      <Box textAlign="center" mb={8}>
        <Text fontWeight="bold" fontSize={{ base: '2xl', md: '5xl' }} color="white" mb={2}>
          Feedback really revs us up!
        </Text>
        <Text fontSize={{ base: 'xl', md: '3xl' }} color="white" mb={2}>
          How likely are you to recommend Lease End to a friend or colleague?
        </Text>
        <Text color="white" fontSize={{ base: 'md', md: '14px' }}>
          (0-10, with 10 being the highest)
        </Text>
      </Box>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ isSubmitting, values, setFieldValue, errors, touched }) => (
          <Form style={{ width: '100%' }}>
            <Flex direction="column" gap="25px" alignItems="center">
              <Gauge
                ref={gaugeRef}
                gaugeLevel={values.rating}
                setGaugeLevel={(value: number) => setFieldValue('rating', value)}
              />
              {values.rating <= 3 && (
                <Box
                  p={2}
                  rounded="md"
                  bg="red.500"
                  color="white"
                  fontWeight="semibold"
                  w="100%"
                  maxW="400px"
                  textAlign="center"
                >
                  {values.rating} out of 10 — looks like we missed the mark. If that's not right,
                  adjust your score above.
                </Box>
              )}
              {touched.rating && errors.rating && (
                <Text color="red.400" fontSize="sm">
                  {errors.rating}
                </Text>
              )}

              <Flex flexDir="column" gap="15px" alignItems="center" w="100%" maxW="400px">
                {!hasDealId && (
                  <>
                    <Flex gap="10px" w="100%" direction={{ base: 'column', md: 'row' }}>
                      <Input
                        name="first_name"
                        placeholder="First Name"
                        label="First name"
                        variant="clay"
                      />
                      <Input
                        name="last_name"
                        placeholder="Last Name"
                        label="Last name"
                        variant="clay"
                      />
                    </Flex>
                    <Input name="email" placeholder="Email" label="Email Address" variant="clay" />
                  </>
                )}

                <TextBox
                  name="feedback"
                  label="What feedback do you have about your experience with Lease End?"
                  placeholder="Share your feedback..."
                  w="100%"
                  maxW="400px"
                  max={1000}
                  showMaxLimit
                  variant="clay"
                />
                <Button
                  type="submit"
                  bg="oceanBoatBlue"
                  color="white"
                  fontWeight="bold"
                  fontSize="16px"
                  w="100%"
                  maxW="400px"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  width="100%"
                  _hover={{
                    transform: 'scale(1.02)',
                    transition: 'all 0.2s ease-in-out',
                  }}
                  _active={{
                    transform: 'scale(0.98)',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Submit Feedback
                </Button>
              </Flex>
            </Flex>
          </Form>
        )}
      </Formik>
      <LowScoreModal
        isOpen={isOpen}
        rating={pendingValues?.rating}
        onFixIt={handleModalFixIt}
        onConfirm={handleModalConfirm}
        isConfirming={isConfirming}
      />
    </Container>
  );
};

export default WeListen;
